(() => {
  if (customElements.get('wf-capabilities-carousel')) return;

  class WFHomeCapabilitiesCarousel extends HTMLElement {
    constructor() {
      super();
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.track = null;
      this.slides = [];
      this.dots = [];
      this.counterCurrent = null;
      this.animationFrame = null;
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-capabilities-track]');
      this.slides = Array.from(this.querySelectorAll('[data-capability-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-capability-dot]'));
      this.counterCurrent = this.querySelector('[data-capability-counter-current]');

      if (!this.track || this.slides.length === 0) return;

      this.addEventListener('click', this.handleDotClick);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.addListener(this.handleMediaChange);
      }

      this.updateMode();
    }

    disconnectedCallback() {
      this.disableMobileMode();
      this.removeEventListener('click', this.handleDotClick);

      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.removeListener(this.handleMediaChange);
      }
    }

    handleMediaChange() {
      this.updateMode();
    }

    handleDotClick(event) {
      const dot = event.target.closest('[data-capability-dot]');
      if (!dot || !this.contains(dot)) return;

      this.goToSlide(Number(dot.dataset.capabilityIndex));
    }

    handleScroll() {
      this.queueActiveSlideUpdate();
    }

    handleResize() {
      this.queueActiveSlideUpdate();
    }

    updateMode() {
      if (this.mobileQuery.matches) {
        this.enableMobileMode();
      } else {
        this.disableMobileMode();
      }
    }

    enableMobileMode() {
      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', 'carousel');
      this.setAttribute('aria-label', this.dataset.carouselLabel || 'Capabilities carousel');
      this.track.tabIndex = 0;
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.queueActiveSlideUpdate();
    }

    disableMobileMode() {
      this.removeAttribute('role');
      this.removeAttribute('aria-roledescription');
      this.removeAttribute('aria-label');
      if (this.track) this.track.tabIndex = -1;
      this.track?.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      if (this.animationFrame !== null) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
    }

    queueActiveSlideUpdate() {
      if (this.animationFrame !== null) return;

      this.animationFrame = window.requestAnimationFrame(() => {
        this.animationFrame = null;
        this.updateActiveSlide();
      });
    }

    updateActiveSlide() {
      if (!this.mobileQuery.matches || !this.track) return;

      const trackStart = this.track.getBoundingClientRect().left;
      let activeIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackStart);

        if (distance < closestDistance) {
          activeIndex = index;
          closestDistance = distance;
        }
      });

      this.setActiveDot(activeIndex);
    }

    setActiveDot(activeIndex) {
      this.dots.forEach((dot, index) => {
        if (index === activeIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.counterCurrent) {
        this.counterCurrent.textContent = String(activeIndex + 1).padStart(2, '0');
      }
    }

    goToSlide(index) {
      if (!this.mobileQuery.matches || !this.track) return;

      const slide = this.slides[index];
      if (!slide) return;

      const trackStart = this.track.getBoundingClientRect().left;
      const slideStart = slide.getBoundingClientRect().left;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.setActiveDot(index);
      this.track.scrollTo({
        left: this.track.scrollLeft + slideStart - trackStart,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('wf-capabilities-carousel', WFHomeCapabilitiesCarousel);
})();
